import { useState, useEffect, useRef } from 'react';
import { useTestStore, type TestId } from '@/store/useTestStore';
import { t } from '@/i18n/translations';
import { TestCard } from '@/components/TestCard';
import { MetricBox } from '@/components/MetricBox';
import { fmtMs, now } from '@/utils/format';

interface Props { autoRun: boolean; autoNonce: number; }

export function TouchTest({ autoRun, autoNonce }: Props) {
  const { language, reportResult, onAutoDone } = useTestStore();
  const [running, setRunning] = useState(false);
  const [metrics, setMetrics] = useState({
    maxTouchPoints: navigator.maxTouchPoints || 0,
    touchEvents: 0,
    avgLatency: null as number | null,
    gesturesDetected: 0,
    lastGesture: '—',
  });
  const latencies = useRef<number[]>([]);
  const lastTouchStart = useRef(0);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);
  const autoDoneRef = useRef(false);
  const areaRef = useRef<HTMLDivElement>(null);

  function reset() {
    setRunning(false);
    autoDoneRef.current = false;
    latencies.current = [];
    setMetrics({ maxTouchPoints: navigator.maxTouchPoints || 0, touchEvents: 0, avgLatency: null, gesturesDetected: 0, lastGesture: '—' });
  }

  function start() {
    reset();
    setRunning(true);
  }

  function stop() {
    setRunning(false);
    const avg = latencies.current.length > 0
      ? latencies.current.reduce((a, b) => a + b, 0) / latencies.current.length
      : null;

    let sc = 50;
    const mtp = navigator.maxTouchPoints || 0;
    if (mtp >= 5) sc += 20;
    else if (mtp >= 2) sc += 10;
    if (avg != null && avg < 20) sc += 20;
    else if (avg != null && avg < 50) sc += 10;
    if (metrics.gesturesDetected > 0) sc += 10;
    sc = Math.min(100, sc);

    const testId: TestId = 'mobile-touch';
    reportResult(testId, {
      score: sc,
      primary: `${mtp} touch points · avg ${fmtMs(avg)}`,
      details: metrics,
    });
    if (autoRun && !autoDoneRef.current) { autoDoneRef.current = true; onAutoDone(testId); }
  }

  function onTouchStart(e: React.TouchEvent) {
    if (!running) return;
    lastTouchStart.current = now();
    if (e.touches.length > 0) {
      touchStartPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    setMetrics((m) => ({ ...m, touchEvents: m.touchEvents + 1 }));
  }

  function onTouchEnd(e: React.TouchEvent) {
    if (!running) return;
    const lat = now() - lastTouchStart.current;
    latencies.current.push(lat);
    const avg = latencies.current.reduce((a, b) => a + b, 0) / latencies.current.length;
    setMetrics((m) => ({ ...m, touchEvents: m.touchEvents + 1, avgLatency: avg }));

    // Detect swipe gesture
    if (touchStartPos.current && e.changedTouches.length > 0) {
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const dx = endX - touchStartPos.current.x;
      const dy = endY - touchStartPos.current.y;
      const dist = Math.hypot(dx, dy);
      if (dist > 50) {
        const gesture = Math.abs(dx) > Math.abs(dy)
          ? (dx > 0 ? 'Swipe Right' : 'Swipe Left')
          : (dy > 0 ? 'Swipe Down' : 'Swipe Up');
        setMetrics((m) => ({ ...m, gesturesDetected: m.gesturesDetected + 1, lastGesture: gesture }));
      }
    }
    touchStartPos.current = null;
  }

  function onTouchMove(e: React.TouchEvent) {
    if (!running) return;
    setMetrics((m) => ({ ...m, touchEvents: m.touchEvents + 1 }));
  }

  // AUTO-RUN: simulate touches aren't possible, so just measure capabilities
  useEffect(() => {
    if (!autoRun) return;
    start();
    const timer = setTimeout(() => {
      // Score based on capabilities alone
      let sc = 40;
      const mtp = navigator.maxTouchPoints || 0;
      if (mtp >= 10) sc = 90;
      else if (mtp >= 5) sc = 80;
      else if (mtp >= 2) sc = 65;
      else if (mtp >= 1) sc = 55;
      else sc = 30;

      setRunning(false);
      const testId: TestId = 'mobile-touch';
      reportResult(testId, {
        score: sc,
        primary: `${mtp} touch points (auto)`,
        details: { maxTouchPoints: mtp, touchSupport: 'ontouchstart' in window },
      });
      if (!autoDoneRef.current) { autoDoneRef.current = true; onAutoDone(testId); }
    }, 2000);
    return () => clearTimeout(timer);
  }, [autoRun, autoNonce]);

  return (
    <TestCard title={t('mobile.touch.title', language)} description={t('mobile.touch.desc', language)}>
      <div className="flex flex-wrap gap-2.5 items-center">
        <button className="btn btn-primary" onClick={start} disabled={running || autoRun}>{t('btn.start', language)}</button>
        <button className="btn btn-danger" onClick={stop} disabled={!running || autoRun}>{t('btn.stop', language)}</button>
        <button className="btn" onClick={reset} disabled={autoRun}>{t('btn.reset', language)}</button>
      </div>
      <div className="grid grid-cols-4 gap-2.5 mt-3 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <MetricBox label={t('touch.maxPoints', language)} value={String(metrics.maxTouchPoints)} />
        <MetricBox label={t('touch.avgLatency', language)} value={fmtMs(metrics.avgLatency)} hint="< 20ms = GOOD" />
        <MetricBox label={t('touch.gestures', language)} value={String(metrics.gesturesDetected)} hint={metrics.lastGesture} />
        <MetricBox label={t('touch.events', language)} value={String(metrics.touchEvents)} />
      </div>
      <div className="h-px bg-border my-3.5" />
      <div
        ref={areaRef}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onTouchMove={onTouchMove}
        className="h-[40vh] min-h-[240px] border-2 border-dashed border-accent/40 rounded-2xl flex items-center justify-center text-muted select-none touch-none"
      >
        {running
          ? t('touch.area', language)
          : autoRun ? t('status.autoRun', language) : t('status.pressStart', language)}
      </div>
    </TestCard>
  );
}
